/**
 * The woo coworking wordmark (03-design-system § 5, board `LogoWordmark`).
 *
 * A lockup of two lines: "w" set in Syne 800 followed by the two rings, the
 * second carrying the lime dot — the "oo" is the mark, not two letters, so it
 * is drawn rather than typed — and under it COWORKING in Figtree 600 capitals,
 * the letters spread across the full width of the line above (the first C under
 * the "w", the last G flush with the outer ring). It is always used as this
 * lockup, however small: the subline is 0.192 × the mark's size and never under
 * 6 px (the brand sheet's rule).
 *
 * `currentColor` on the strokes and the letters lets it sit on the teal panel,
 * on white and on the ink footer without a second copy; the dot stays lime
 * either way, because that is the one highlight the brand allows per screen.
 *
 * The letters are consts, not JSX literals: a brand mark is not translatable
 * copy, which is the convention the admin shell already follows. The "oo" is
 * drawn, so the accessible name is spelled out on its own.
 */
const WORDMARK_LETTER = 'w';
const WORDMARK_SUBLINE = 'COWORKING';
const WORDMARK_NAME = 'woo coworking';

/** Subline size relative to the mark (48 / 250 on the board) and its floor. */
const SUBLINE_RATIO = 0.192;
const SUBLINE_MIN = 6;
/** Gap between the mark and the subline (16 / 250 on the board). */
const SUBLINE_GAP_RATIO = 0.064;

export function Wordmark({ className, size = 40 }: { className?: string; size?: number }) {
  const subline = Math.max(SUBLINE_MIN, size * SUBLINE_RATIO);
  return (
    <span
      className={`font-heading inline-flex w-max flex-col items-stretch whitespace-nowrap font-extrabold leading-none ${className ?? ''}`}
      style={{ fontSize: `${size}px`, letterSpacing: '-0.03em' }}
    >
      <span className="flex items-center" aria-hidden>
        <span>{WORDMARK_LETTER}</span>
        <Rings />
      </span>
      <span
        className="font-body flex items-baseline justify-between font-semibold"
        style={{ marginTop: `${size * SUBLINE_GAP_RATIO}px`, fontSize: `${subline}px`, letterSpacing: 0 }}
        aria-hidden
      >
        {WORDMARK_SUBLINE.split('').map((letter, index) => (
          <span key={index}>{letter}</span>
        ))}
      </span>
      <span className="sr-only">{WORDMARK_NAME}</span>
    </span>
  );
}

/**
 * The two rings of the wordmark, drawn in the current colour at 0.6 em of the
 * surrounding type — the same geometry on the mark and inside the claim.
 */
function Rings({ inline = false }: { inline?: boolean }) {
  return (
    <svg
      viewBox="0 0 84 48"
      aria-hidden
      style={
        inline
          ? {
              height: '0.6em',
              width: 'auto',
              display: 'inline-block',
              verticalAlign: 'baseline',
              margin: '0 0.02em 0 0.03em',
              overflow: 'visible',
            }
          : { height: '0.6em', width: 'auto', display: 'block', marginLeft: '0.03em', marginTop: '0.02em' }
      }
    >
      <circle cx="23" cy="24" r="17" fill="none" stroke="currentColor" strokeWidth="11" />
      <circle cx="61" cy="24" r="17" fill="none" stroke="currentColor" strokeWidth="11" />
      <circle cx="61" cy="24" r="6.5" className="fill-lime" />
    </svg>
  );
}

/**
 * The claim with the wordmark's "oo" drawn in it (03 § 5 `ClaimWoo`, board
 * `LogoClaim`): "W", the two rings, " arbeitest du heute?" as one line of type.
 * Takes the whole string from `de.json` and replaces its first "oo"; a string
 * without one is printed as it is. The rings follow the text's colour and size,
 * so the same component serves the 88 px hero and the footer's 15 px line.
 *
 * Screen readers get the full sentence once; the drawn parts are hidden from
 * them, or the "W" and the rest would be read as two words with a hole.
 */
export function ClaimWoo({ text, className }: { text: string; className?: string }) {
  const at = text.indexOf('oo');
  if (at < 0) return <span className={className}>{text}</span>;
  return (
    <span className={className}>
      <span aria-hidden className="whitespace-nowrap">
        {text.slice(0, at)}
        <Rings inline />
      </span>
      <span aria-hidden>{text.slice(at + 2)}</span>
      <span className="sr-only">{text}</span>
    </span>
  );
}

/** The Apple glyph of the "Mit Apple fortfahren" button (09 § 4). */
export function AppleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden className="block flex-none">
      <path
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
        fill="currentColor"
      />
    </svg>
  );
}

/** The Google mark. Its four brand colours are fixed by Google, not by us. */
export function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden className="block flex-none">
      <path
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
        fill="#4285F4"
      />
      <path
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
        fill="#34A853"
      />
      <path
        d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
        fill="#FBBC05"
      />
      <path
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
        fill="#EA4335"
      />
    </svg>
  );
}
